import { Lead } from '../types/lead';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Separator } from './ui/separator';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { 
  ArrowLeft, 
  MapPin, 
  Calendar, 
  DollarSign, 
  Phone, 
  Mail, 
  Home, 
  Star, 
  CheckCircle2,
  Clock,
  TrendingUp,
  PlayCircle,
  Images as ImagesIcon,
  User,
  AlertCircle,
  Wrench
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { MediaViewer } from './MediaViewer';
import { JwtPayload, jwtDecode } from 'jwt-decode';
import ChatModal from 'Common/ChatModal';
import { CallAPIGetPromise } from 'Common/commaon';
import { getDecodeId } from 'slices/userProfile/thunk';
import { HomeownerInfoShimmer } from './shimmer';

interface LeadDetailsProps {
  lead: any;
  onBack: () => void;
  onApplyLead?: (leadId: string) => void;
  showApplyButton?: boolean;
  onDeclineLead?:(leadId: string) => void;
  canRespondToJobs?: boolean;
  canAcceptJobs?: boolean;
}

export function LeadDetails({ lead, onBack, onApplyLead, onDeclineLead,showApplyButton = false, canRespondToJobs = false, canAcceptJobs = false }: LeadDetailsProps) {
  const [isMediaViewerOpen, setIsMediaViewerOpen] = useState(false);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [chat, setChat] = useState<any>(false);
  const [savedCards,setSavedCards]=useState([])
  const [loading,setLoading]=useState(false)
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'default';
      case 'requested':
        return 'secondary';
      default:
        return 'default';
    }
  };
  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      plumbing: 'bg-blue-100 text-blue-800',
      electrical: 'bg-yellow-100 text-yellow-800',
      carpentry: 'bg-amber-100 text-amber-800',
      hvac: 'bg-cyan-100 text-cyan-800',
      painting: 'bg-purple-100 text-purple-800',
      roofing: 'bg-slate-100 text-slate-800',
      landscaping: 'bg-green-100 text-green-800',
      general: 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors.general;
  };
useEffect(()=>{
  setLoading(true)
  CallAPIGetPromise(`/stripe/api/get-saved-cards/${getDecodeId()}`).then((res:any)=>{
    if(res?.data){
      setSavedCards(res.data)
    }
    }).finally(()=>{
      setLoading(false)
    }).catch(()=>{
      
    })
},[])
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get media items (prefer media array, fallback to images)
  const mediaItems = lead.media || (lead.images?.map((url:any) => ({ type: 'image' as const, url })) || []);
  const hasMedia = mediaItems.length > 0;

  const handleMediaClick = (index: number) => {
    setSelectedMediaIndex(index);
    setIsMediaViewerOpen(true);
  };
  const token = localStorage.getItem("authUser");
  let decoded: any = "";
  if (token !== null) {
    decoded = jwtDecode<JwtPayload>(token);
  } else {
    // console.error("Token is null");
  }
  const userName  = decoded.userType === "homeowner" ?decoded?.user_name:lead.homeowner.user_first_name
  const userType  = decoded.userType === "homeowner" ?"Contractor":"Homeowner"
  return (
    <>
      <div className="lead-details lead-management-content">
        <div className="max-w-5xl mx-auto">
          {/* Back Button */}
          <Button 
            variant="ghost" 
            onClick={onBack}
            className="mb-6"
          >
            <ArrowLeft className="size-4 mr-2" />
            Back to Lead
          </Button>

          {/* Hero Image */}
          {hasMedia && (
  <div
    style={{
      position: "relative",
      height: "24rem", // h-96
      borderRadius: "1rem",
      overflow: "hidden",
      marginBottom: "1.5rem",
      boxShadow: "0 10px 25px rgba(0,0,0,0.3)",
      cursor: "pointer",
      transition: "transform 0.3s ease",
    }}
    onClick={() => handleMediaClick(0)}
    onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.01)")}
    onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
  >
    {/* Media Image / Video Thumbnail */}
    {/* <img
      src={mediaItems[0].url}
      alt={lead.title}
      style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        transition: "transform 0.5s ease",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
      onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
    /> */}
 {mediaItems[0].type === 'image' ? (
                  <img
                    src={mediaItems[0].url}
                    alt=''
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      transition: "transform 0.5s ease",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
                    onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                  />
                ) : (
                  <video
                    src={mediaItems[0].url}
                    controls
                    autoPlay
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      transition: "transform 0.5s ease",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
                    onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                  />
                )}
    {/* Gradient Overlay */}
    <div
      style={{
        position: "absolute",
        inset: 0,
        background:
          "linear-gradient(to top, rgba(0,0,0,0.8), rgba(0,0,0,0.4), transparent)",
      }}
    />

    {/* Click-to-View Overlay */}
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0,0,0,0.3)",
        backdropFilter: "blur(4px)",
        opacity: 0,
        transition: "opacity 0.3s ease",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
      onMouseLeave={(e) => (e.currentTarget.style.opacity = "0")}
    >
      {mediaItems[0].type === "video" ? (
        <div style={{ color: "#fff", textAlign: "center" }}>
          <PlayCircle style={{ width: 80, height: 80, margin: "0 auto 12px" }} />
          <p style={{ fontSize: "1.25rem" }}>Click to play video</p>
        </div>
      ) : (
        <div style={{ color: "#fff", textAlign: "center" }}>
          <ImagesIcon style={{ width: 80, height: 80, margin: "0 auto 12px" }} />
          <p style={{ fontSize: "1.25rem" }}>Click to view full screen</p>
        </div>
      )}
    </div>

    {/* Media Counter Badge */}
    {mediaItems.length > 1 && (
      <div
        style={{
          position: "absolute",
          top: "1.5rem",
          right: "1.5rem",
          background: "rgba(0,0,0,0.7)",
          color: "#fff",
          borderRadius: "0.5rem",
          padding: "0.5rem 1rem",
          display: "flex",
          alignItems: "center",
          fontSize: "1rem",
          backdropFilter: "blur(4px)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
        }}
      >
        <ImagesIcon style={{ width: 16, height: 16, marginRight: 8 }} />
        {mediaItems.length} media files
      </div>
    )}

    {/* Bottom Info Overlay */}
    <div
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        padding: "2rem",
        color: "#fff",
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "1rem" }}>
 

        <Badge
          style={{
            background: getStatusColor(lead.status),
            color: "#fff",
            fontSize: "1rem",
            padding: "0.4rem 1rem",
            borderRadius: "0.4rem",
          }}
        >
          {lead.status.charAt(0).toUpperCase() + lead.status.slice(1)}
        </Badge>
      </div>

      <h1 style={{ color: "#fff", marginBottom: "0.75rem", fontSize: "1.5rem", fontWeight: 600 }}>
        {lead.title}
      </h1>

      <div style={{ display: "flex", alignItems: "center", gap: "1.5rem", color: "rgba(255,255,255,0.9)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <DollarSign style={{ width: 20, height: 20 }} />
          <span style={{ fontSize: "1rem" }}>${Number(lead.budget).toLocaleString()}</span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <MapPin style={{ width: 20, height: 20 }} />
          <span style={{ fontSize: "1rem" }}>
            {lead.location.city}, {lead.location.state}
          </span>
        </div>
      </div>
    </div>
  </div>
)}

          {/* Main Lead Card */}
          <Card className="lead-details-card mb-6 shadow-lg">
            <CardHeader>
              {!lead.images && (
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <Badge className={getCategoryColor(lead.category)}>
                        {lead.category.charAt(0).toUpperCase() + lead.category.slice(1)}
                      </Badge>
                      <Badge variant={getStatusColor(lead.status)}>
                        {lead.status.charAt(0).toUpperCase() + lead.status.slice(1)}
                      </Badge>
                    </div>
                    <CardTitle className="mb-2">{lead.title}</CardTitle>
                  </div>
                </div>
              )}
              <CardDescription className="text-lg">{lead.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <DollarSign className="size-5 text-green-700" />
                  </div>
                  <div>
                    <div className="text-muted-foreground">Budget Range</div>
                    <p>${Number(lead.budget).toLocaleString() }</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <MapPin className="size-5 text-blue-700" />
                  </div>
                  <div>
                    <div className="text-muted-foreground">Location</div>
                    <p>{lead.location.city}, {lead.location.state}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Clock className="size-5 text-purple-700" />
                  </div>
                  <div>
                    <div className="text-muted-foreground">Posted Date</div>
                    <p>{formatDate(lead.postedDate)}</p>
                  </div>
                </div>

                {lead.deadline && (
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <Calendar className="size-5 text-orange-700" />
                    </div>
                    <div>
                      <div className="text-muted-foreground">Deadline</div>
                      <p>{formatDate(lead.deadline)}</p>
                    </div>
                  </div>
                )}

                {lead.requestedDate && (
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <CheckCircle2 className="size-5 text-green-700" />
                    </div>
                    <div>
                      <div className="text-muted-foreground">Requested On</div>
                      <p>{formatDate(lead.requestedDate)}</p>
                    </div>
                  </div>
                )}
              </div>

              <Separator className="my-6" />

              {/* Requirements */}
              {/* <div className="mb-6">
                <h3 className="mb-3">Project Requirements</h3>
                <ul className="space-y-2">
                  {lead.requirements.map((requirement, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle2 className="size-4 text-green-600 mt-1 flex-shrink-0" />
                      <span>{requirement}</span>
                    </li>
                  ))}
                </ul>
              </div> */}

{showApplyButton && onApplyLead && canAcceptJobs && (
                <Button 
                  onClick={() => onApplyLead(lead.id)}
                  className="w-full"
                  size="lg"
                >
                  Apply for This Lead
                </Button>
              )}  
              {onDeclineLead && canRespondToJobs && (
                <Button 
                  onClick={() => onDeclineLead(lead.id)}
                  className="w-full mt-2 text-red-500 bg-red-50 border"
                  size="lg"
                  variant={"ghost"}
                >
                  Decline
                </Button>
              )}

            </CardContent>
          </Card>
{loading && <HomeownerInfoShimmer />}
            { !loading && lead.lead_sub_cat && lead.lead_sub_cat.length > 0 && (
              <div className="lead-details-services bg-white border mb-4 border-gray-200 rounded-lg p-6">
                <div className="flex items-center mb-2">
                  <Wrench className="w-5 h-5 mr-2 text-gray-700" />
                  <h3 className="font-semibold text-lg">Services Included</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {lead.lead_sub_cat.map((service:any) => (
                    <div
                      key={service.id}
                      className="border border-blue-200 bg-blue-50 rounded-lg p-4 hover:shadow-sm transition-shadow"
                    >
                      <div className="font-medium text-blue-900 mb-1">{service.name}</div>
                    
                    </div>
                  ))}
                </div>
              </div>
            )}
          {savedCards[0] && !loading &&    <Card>
            <CardHeader>
              <CardTitle>Homeowner Information</CardTitle>
              <CardDescription>Details about the person posting this lead</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-4 mb-6">
                <Avatar className="size-16">
                  {lead.homeowner.pic ?  <img
              src={lead.homeowner.pic}
              alt={lead.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            /> :<AvatarFallback className="bg-blue-600 text-white text-lg">
            {getInitials(lead.homeowner.name)}
          </AvatarFallback>}
                  
                </Avatar>
                <div className="flex-1">
                  <h3 className="mb-1">{lead.homeowner.name}</h3>
                  {/* <div className="flex items-center">
                   {lead.homeowner.completedProjects >0 &&<span className="text-muted-foreground">
                      ({lead.homeowner.completedProjects} completed project{lead.homeowner.completedProjects !== 1 ? 's' : ''})
                    </span>}
                    
                  </div> */}
                  <div className="text-muted-foreground">
                    Member since {new Date(lead.homeowner.memberSince).toLocaleDateString('en-US', { 
                      month: 'long', 
                      year: 'numeric' 
                    })}
                  </div>
                </div>
              </div>

              <Separator className="my-6" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Mail className="size-5 text-blue-700" />
                  </div>
                  <div>
                    <div className="text-muted-foreground">Email</div>
                    <p>{lead.homeowner.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Phone className="size-5 text-green-700" />
                  </div>
                  <div>
                    <div className="text-muted-foreground">Phone</div>
                    <p>{lead.homeowner.phone}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Home className="size-5 text-purple-700" />
                  </div>
                  <div>
                    <div className="text-muted-foreground">Address</div>
                    <p>{lead.location.address},{lead.location.city}, {lead.location.state}, {lead.location.zipCode}</p>
                  </div>
                </div>

                {/* <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <User className="size-5 text-orange-700" />
                  </div>
                  <div>
                    <div className="text-muted-foreground">Completed Projects</div>
                    <p>{lead.homeowner.completedProjects} projects</p>
                  </div>
                </div> */}
              </div>



          
                         <Button 
                  onClick={() => setChat(true)}
                  className="w-full mt-2"
                  size="lg"
                >
                  Message
                </Button>
            </CardContent>
          </Card>}
      
        </div>
      </div>
      {isMediaViewerOpen && (
        <MediaViewer
          mediaItems={mediaItems}
          initialIndex={selectedMediaIndex}
          onClose={() => setIsMediaViewerOpen(false)}
        />
      )}
      
      <ChatModal project={{
        id:lead.id,
        lead_sub_cat:lead.lead_sub_cat,
      }} userName={userName}  userType={userType} OtherId={lead.homeowner.id} show={chat} handleClose={()=>{
        setChat(false)
      }} />

    </>
  );
}
